<template>
  <q-dialog v-model="isOpen" class="windowed-dialog" content-class="z-max" seamless>
    <q-card style="min-width: 180px; max-width: 100%">
      <q-card-section class="row items-center no-wrap">
        <div class="text-h6">Choose Background</div>
        <q-space />
        <q-btn dense flat round icon="close" @click="close" />
      </q-card-section>

      <q-separator />

      <q-card-section>
        <div class="q-mb-md column items-center justify-center text-center">
          <div class="text-caption q-mb-sm">Pick a built-in background or upload your own.</div>
          <div class="row items-center justify-center q-gutter-sm">
            <q-btn dense label="Upload" icon="upload" @click="triggerUpload" />
            <input ref="fileInput" class="hidden" type="file" accept="image/*" @change="onFile" />
          </div>
        </div>

        <div class="text-subtitle2 q-mb-xs hidden" v-if="builtInOptions.length">Built-in</div>
        <div class="grid q-mb-md" v-if="builtInOptions.length">
          <div
            v-for="opt in builtInOptions"
            :key="opt.id"
            class="tile"
            :class="{ selected: localSelectedId === opt.id }"
            @click="choose(opt)"
          >
            <q-img :src="opt.src" :ratio="16 / 9" spinner-color="grey-5" />
            <div class="label ellipsis">{{ opt.label }}</div>
          </div>
        </div>

        <div class="text-subtitle2 q-mb-xs" v-if="localOptions.length">My Backgrounds</div>
        <div class="grid" v-if="localOptions.length">
          <div
            v-for="opt in localOptions"
            :key="opt.id"
            class="tile"
            :class="{ selected: localSelectedId === opt.id }"
            @click="choose(opt)"
          >
            <div class="tile__actions">
              <q-btn
                flat
                dense
                round
                size="sm"
                icon="delete"
                color="negative"
                @click.stop="onDeleteLocal(opt)"
              />
            </div>
            <q-img :src="opt.src" :ratio="16 / 9" spinner-color="grey-5" />
            <div class="label ellipsis">{{ opt.label || opt.id }}</div>
          </div>
        </div>

        <div
          v-if="!lib.loading && builtInOptions.length === 0 && localOptions.length === 0"
          class="text-caption text-grey"
        >
          No backgrounds yet. Use Upload to add one.
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="close" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
  import { defineComponent, ref, watch, onMounted, computed } from 'vue'
  import { useBackgroundLibrary } from '../../../composables/useBackgroundLibrary'
  import { CHAT_BACKGROUND_FILES, CHAT_BACKGROUND_PUBLIC_PATH } from '../../../config/backgrounds'

  export default defineComponent({
    name: 'BackgroundPickerDialog',
    props: {
      modelValue: { type: Boolean, default: false },
      selectedId: { type: String, default: '' }
    },
    emits: ['update:modelValue', 'select', 'cancel'],
    setup(props, { emit }) {
      const isOpen = ref(!!props.modelValue)
      // try {
      //   console.debug('[BackgroundPickerDialog] setup - initial modelValue=', props.modelValue)
      // } catch {}
      watch(
        () => props.modelValue,
        (v) => {
          try {
            console.debug('[BackgroundPickerDialog] props.modelValue changed ->', v)
          } catch {}
          isOpen.value = !!v
        }
      )
      watch(isOpen, (v) => {
        try {
          console.debug('[BackgroundPickerDialog] isOpen changed ->', v)
        } catch {}
        emit('update:modelValue', !!v)
      })

      const lib = useBackgroundLibrary()
      onMounted(() => {
        // try {
        //   console.debug('[BackgroundPickerDialog] mounted')
        // } catch {}
        lib.load().catch(() => {})
      })

      const localOptions = computed(() => {
        try {
          const arr = Array.isArray(lib.options?.value)
            ? lib.options.value
            : Array.isArray(lib.options)
              ? lib.options
              : []
          // console.debug('[BackgroundPickerDialog] localOptions length =', arr.length)
          return arr
        } catch {
          return []
        }
      })

      const toLabel = (name) => {
        try {
          const base = String(name).replace(/\.[^.]+$/, '')
          return base.replace(/[_-]+/g, ' ')
        } catch {
          return name
        }
      }
      const builtInOptions = computed(() => {
        const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '')
        const prefix = String(CHAT_BACKGROUND_PUBLIC_PATH || 'images/chat_background').replace(
          /^\/+/,
          ''
        )
        return (CHAT_BACKGROUND_FILES || []).map((f) => ({
          id: `builtin:${f}`,
          label: toLabel(f),
          // Build URL relative to app base so it works on localhost and subpaths
          src: `${base}/${prefix}/${f}`
        }))
      })

      const localSelectedId = ref(props.selectedId || '')
      watch(
        () => props.selectedId,
        (v) => (localSelectedId.value = v || '')
      )

      const fileInput = ref(null)
      const uploading = ref(false)
      const triggerUpload = () => {
        if (fileInput.value) fileInput.value.click()
      }
      const onFile = async (evt) => {
        const file = evt?.target?.files?.[0]
        if (!file) return
        try {
          uploading.value = true
          const added = await lib.upload(file)
          localSelectedId.value = added?.id || localSelectedId.value
          // Do not auto-close; keep dialog open so the new item is visible under "My Backgrounds"
          if (added?.id) emit('select', { id: added.id, option: added })
        } catch {}
        try {
          evt.target.value = ''
        } catch {}
        uploading.value = false
      }

      const close = () => {
        isOpen.value = false
        emit('cancel')
      }
      const confirm = () => {
        // Retained for compatibility; no longer used (selection happens on click)
        const id = localSelectedId.value
        let opt = null
        if (id) {
          if (id.startsWith('builtin:')) opt = builtInOptions.value.find((o) => o.id === id) || null
          else opt = lib.getById(id)
        }
        if (id) emit('select', { id, option: opt })
        isOpen.value = false
      }

      const choose = (opt) => {
        try {
          const id = opt?.id || ''
          localSelectedId.value = id
          emit('select', { id, option: opt || null })
        } finally {
          isOpen.value = false
        }
      }

      const onDeleteLocal = async (opt) => {
        if (!opt?.id) return
        try {
          const ok = await lib.remove(opt.id)
          if (ok && localSelectedId.value === opt.id) {
            localSelectedId.value = ''
          }
        } catch {
          /* ignore */
        }
      }

      return {
        isOpen,
        lib,
        localOptions,
        builtInOptions,
        localSelectedId,
        fileInput,
        triggerUpload,
        onFile,
        uploading,
        onDeleteLocal,
        choose,
        close,
        confirm
      }
    }
  })
</script>

<style scoped>
  .hidden {
    display: none;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
  }
  .tile {
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    position: relative;
  }
  .tile.selected {
    outline: 2px solid var(--q-primary);
  }
  .tile__actions {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
    display: flex;
    gap: 4px;
  }
  .label {
    padding: 6px 8px;
    font-size: 12px;
    background: rgba(0, 0, 0, 0.3);
  }
</style>
